export const SIGNS = ['Aries','Taurus','Gemini','Cancer','Leo','Virgo','Libra','Scorpio','Sagittarius','Capricorn','Aquarius','Pisces'] as const;
export const SIGN_GLYPHS = ['♈','♉','♊','♋','♌','♍','♎','♏','♐','♑','♒','♓'];
export const SIGN_ELEMENT = ['Fire','Earth','Air','Water','Fire','Earth','Air','Water','Fire','Earth','Air','Water'] as const;
export const SIGN_MODALITY = ['Cardinal','Fixed','Mutable','Cardinal','Fixed','Mutable','Cardinal','Fixed','Mutable','Cardinal','Fixed','Mutable'] as const;

export const PLANETS = [
  { name: 'Sun',     glyph: '☉', color: 'hsl(38 70% 65%)' },
  { name: 'Moon',    glyph: '☾', color: 'hsl(45 25% 80%)' },
  { name: 'Mercury', glyph: '☿', color: 'hsl(195 35% 70%)' },
  { name: 'Venus',   glyph: '♀', color: 'hsl(330 50% 75%)' },
  { name: 'Mars',    glyph: '♂', color: 'hsl(8 60% 60%)' },
  { name: 'Jupiter', glyph: '♃', color: 'hsl(38 50% 60%)' },
  { name: 'Saturn',  glyph: '♄', color: 'hsl(40 25% 50%)' },
  { name: 'Uranus',  glyph: '♅', color: 'hsl(190 40% 60%)' },
  { name: 'Neptune', glyph: '♆', color: 'hsl(220 40% 60%)' },
  { name: 'Pluto',   glyph: '♇', color: 'hsl(310 25% 55%)' },
  { name: 'Chiron',  glyph: '⚷', color: 'hsl(95 25% 55%)' },
  { name: 'Node',    glyph: '☊', color: 'hsl(40 30% 65%)' },
  { name: 'SouthNode', glyph: '☋', color: 'hsl(40 20% 50%)' },
  { name: 'Lilith',  glyph: '⚸', color: 'hsl(275 30% 65%)' },
  { name: 'Fortune', glyph: '⊗', color: 'hsl(50 45% 70%)' },
] as const;

export const ALL_POINTS = ['Sun','Moon','Mercury','Venus','Mars','Jupiter','Saturn','Uranus','Neptune','Pluto','Chiron','Node','SouthNode','Lilith'];
export const CORE_POINTS = ['Sun','Moon','Mercury','Venus','Mars','Jupiter','Saturn','Uranus','Neptune','Pluto'];

/**
 * Per-body orb multiplier. The luminaries carry the widest orbs; derived or
 * low-precision points (nodes, Chiron, Lilith, Fortune) are kept tight so they
 * do not flood a chart with weak, unfelt aspects.
 */
export const ORB_FACTORS: Record<string, number> = {
  Sun: 1.15, Moon: 1.15,
  Chiron: 0.6, Node: 0.6, SouthNode: 0.6, Lilith: 0.5, Fortune: 0.5,
};

/** Pairs whose angle is fixed by definition — never a meaningful aspect. */
export const DEFINITIONAL_PAIRS: ReadonlyArray<readonly [string, string]> = [
  ['Node', 'SouthNode'],
  ['Asc', 'MC'],
];

export const PLANET_MEANINGS: Record<string, { title: string; short: string; icon: string }> = {
  Sun: { title: 'Your Core Self', short: 'who you fundamentally are', icon: 'the bright center of who you are' },
  Moon: { title: 'Your Inner World', short: 'how you feel and what you need', icon: 'the private part of you that only close people see' },
  Mercury: { title: 'How You Think', short: 'your mind and how you communicate', icon: 'the voice in your head and how it comes out' },
  Venus: { title: 'What You Love', short: 'how you love and what you find beautiful', icon: 'what draws you in and what you cherish' },
  Mars: { title: 'Your Drive', short: 'what makes you act and what you fight for', icon: 'the fire that gets you out of bed' },
  Jupiter: { title: 'Your Luck', short: 'where life opens up for you', icon: 'where things come easily and you grow' },
  Saturn: { title: 'Your Challenges', short: 'what you must master through effort', icon: 'where life asks you to become strong' },
  Uranus: { title: 'Your Uniqueness', short: 'how you break the mold', icon: "the part of you that doesn't fit any box" },
  Neptune: { title: 'Your Dreams', short: 'your imagination and spirituality', icon: 'where reality blurs into something bigger' },
  Pluto: { title: 'Your Depths', short: 'where you transform and reclaim power', icon: 'the parts of you that go through death and rebirth' },
  Chiron: { title: 'Your Wound', short: 'the deep hurt that becomes your gift', icon: 'the place that hurts and also heals others' },
  Node: { title: 'Your Direction', short: 'where your soul is growing toward', icon: "the horizon you're walking toward in this life" },
  Fortune: { title: 'Your Point of Joy', short: 'where ease, luck, and natural fulfillment live', icon: 'the place where Sun, Moon, and Ascendant align into wellbeing' },
};

export const SIGN_MEANINGS: Record<string, { keyword: string; traits: string; element: string }> = {
  Aries: { keyword: 'the pioneer', traits: 'bold, direct, impatient, a natural starter', element: 'fire' },
  Taurus: { keyword: 'the builder', traits: 'steady, sensual, patient, a lover of comfort', element: 'earth' },
  Gemini: { keyword: 'the messenger', traits: 'curious, talkative, quick-witted, easily bored', element: 'air' },
  Cancer: { keyword: 'the nurturer', traits: 'caring, moody, protective, deeply feeling', element: 'water' },
  Leo: { keyword: 'the performer', traits: 'warm, proud, generous, loves attention', element: 'fire' },
  Virgo: { keyword: 'the perfectionist', traits: 'careful, analytical, helpful, self-critical', element: 'earth' },
  Libra: { keyword: 'the diplomat', traits: 'charming, fair-minded, indecisive, relationship-focused', element: 'air' },
  Scorpio: { keyword: 'the investigator', traits: 'intense, private, perceptive, all-or-nothing', element: 'water' },
  Sagittarius: { keyword: 'the seeker', traits: 'optimistic, restless, philosophical, loves freedom', element: 'fire' },
  Capricorn: { keyword: 'the strategist', traits: 'disciplined, ambitious, reserved, plays the long game', element: 'earth' },
  Aquarius: { keyword: 'the rebel', traits: 'independent, unconventional, humanitarian, emotionally cool', element: 'air' },
  Pisces: { keyword: 'the dreamer', traits: 'empathetic, imaginative, boundary-less, sensitive to everything', element: 'water' },
};

/**
 * Per-planet framing for the Big Three cards. Each entry pairs a "lens"
 * (what this point describes about you) with a sign-flavor verb so that
 * Sun and Moon read distinctly even when they share a sign.
 */
export const BIG_THREE_FRAMING: Record<'Sun' | 'Moon' | 'Rising', {
  lens: string;
  verb: string;
}> = {
  Sun: {
    lens: 'Your core identity — the engine of who you are becoming.',
    verb: 'expresses as',
  },
  Moon: {
    lens: 'Your emotional inner world — what you need to feel safe and at home.',
    verb: 'feels safe through',
  },
  Rising: {
    lens: 'How you meet the world — the first impression you make.',
    verb: 'meets the world as',
  },
};

/**
 * Per-(planet, sign) body copy for the Big Three cards.
 * Distinct paragraph per pair so Sun-in-X and Moon-in-X read differently
 * even when the sign is identical.
 */
type SignName = typeof SIGNS[number];
export const BIG_THREE_BODY_COPY: Record<'Sun' | 'Moon' | 'Rising', Record<SignName, string>> = {
  Sun: {
    Aries: 'Your core self runs on initiative and heat — you become yourself by going first, even when no one asked.',
    Taurus: 'Your core ambition is patient and embodied — you build a self the way others build houses, one steady decision at a time.',
    Gemini: 'Your core self is plural and curious — identity, for you, is the conversation between many possible versions of you.',
    Cancer: 'Your core self is a quiet keeper of memory — you become yourself by tending to what (and whom) you love.',
    Leo: 'Your core self wants to be seen on purpose — you grow by daring to take up the room you actually need.',
    Virgo: 'Your core self refines as it goes — you become yourself by paying close attention and quietly improving the work.',
    Libra: 'Your core self is shaped in relation — you discover who you are by the quality of the company you keep.',
    Scorpio: 'Your core self runs deep and undercover — you become yourself by surviving what most people would rather avoid.',
    Sagittarius: 'Your core self needs horizon — you grow by leaving the familiar long enough to find out what is true.',
    Capricorn: 'Your core ambition runs cold and patient — you measure success in decades and quietly build it that way.',
    Aquarius: 'Your core self is a private signal in a noisy room — you become yourself by refusing to round off your edges.',
    Pisces: 'Your core self is porous and imaginative — you become yourself by learning when to merge and when to surface.',
  },
  Moon: {
    Aries: 'Your emotional world runs hot and fast — you feel safest when you can act on a feeling instead of sit with it.',
    Taurus: 'Your emotional safety lives in the body — touch, food, slowness, and the same chair on the same evening.',
    Gemini: 'Your inner world settles by talking it out — you regulate through conversation, lists, and naming the thing.',
    Cancer: 'Your emotional life is tidal and deeply attached — home, kin, and a few trusted people are how you stay regulated.',
    Leo: 'You feel safe when you are loved out loud — quiet love feels like absence; warmth needs to be visible to count.',
    Virgo: 'Your inner world calms through small order — clean kitchen, finished list, the body taken care of in plain ways.',
    Libra: 'Your emotional safety comes through harmony — conflict in the room destabilises you faster than most people understand.',
    Scorpio: 'Your inner world is private and unflinching — you feel safe with people who can hold what you actually feel.',
    Sagittarius: 'You regulate through movement and meaning — being stuck somewhere small, with no horizon, is its own kind of grief.',
    Capricorn: 'Your emotional safety comes from competence and structure — knowing the plan settles you more than warmth does.',
    Aquarius: 'Your inner world prefers air around it — closeness on your terms, with room to step back without it meaning anything.',
    Pisces: 'Your emotional world is unbounded — you absorb the room and need solitude, water, or art to drain off what is not yours.',
  },
  Rising: {
    Aries: 'You enter rooms forward — direct, a little impatient, and read as someone who would rather act than wait.',
    Taurus: 'You arrive grounded — calm voice, considered movement, and a sensual steadiness people lean toward.',
    Gemini: 'You arrive talking — quick eyes, quick mouth, and a likeable habit of finding the thread in any room.',
    Cancer: 'You arrive softly — watchful, a little protective at first, and warmer than the first impression suggests.',
    Leo: 'You arrive lit from inside — generous, theatrical, and noticeable even when you are trying not to be.',
    Virgo: 'You arrive observant — neat, modest, slightly reserved, and read by others as someone who actually pays attention.',
    Libra: 'You arrive considerate — graceful manner, easy charm, and a near-instinct for keeping a room comfortable.',
    Scorpio: 'You arrive watchful — composed surface, deep underneath, and a presence people register before you say a word.',
    Sagittarius: 'You arrive open — frank, faintly restless, and read as someone who has either just been somewhere or is about to leave.',
    Capricorn: 'You arrive contained — measured, dry-humoured, and read as more competent and older than you actually are.',
    Aquarius: 'You arrive a little sideways — friendly but not quite of the room, with an off-centre style that sticks in memory.',
    Pisces: 'You arrive permeable — gentle eyes, soft edges, and a presence that absorbs the mood of the room before shaping it.',
  },
};

export const HOUSE_MEANINGS: Record<number, { title: string; short: string }> = {
  1: { title: 'Identity & First Impressions', short: 'how the world sees you' },
  2: { title: 'Money & Self-Worth', short: 'what you value and own' },
  3: { title: 'Communication & Siblings', short: 'how you talk and learn' },
  4: { title: 'Home & Family', short: 'where you come from, your roots' },
  5: { title: 'Romance & Creativity', short: 'play, dating, self-expression, children' },
  6: { title: 'Work & Health', short: 'daily routines and the body' },
  7: { title: 'Partnerships', short: 'marriage, committed relationships' },
  8: { title: 'Intimacy & Transformation', short: 'shared money, sex, crisis, rebirth' },
  9: { title: 'Beliefs & Adventure', short: 'travel, higher learning, philosophy' },
  10: { title: 'Career & Public Image', short: 'your place in the world' },
  11: { title: 'Friends & Future', short: 'community, hopes, the long view' },
  12: { title: 'The Hidden Self', short: 'the unconscious, solitude, spirituality' },
};

export const ASPECT_MEANINGS: Record<string, { quality: string; feel: string; kind: string }> = {
  Conjunction: { quality: 'fusion', feel: 'these two blend and act as one force in you', kind: 'powerful' },
  Opposition: { quality: 'tension', feel: 'these two pull in opposite directions, asking you to find balance', kind: 'challenging' },
  Trine: { quality: 'flow', feel: 'these two work together easily — a natural gift', kind: 'easy' },
  Square: { quality: 'friction', feel: 'these two create internal conflict that pushes you to grow', kind: 'challenging' },
  Sextile: { quality: 'opportunity', feel: 'these two cooperate when you make the effort to use them', kind: 'easy' },
  Quincunx: { quality: 'awkwardness', feel: "these two don't quite fit together — requires adjustment", kind: 'subtle' },
  Sesquiquadrate: { quality: 'irritation', feel: 'low-grade tension that builds over time', kind: 'subtle' },
  Semisquare: { quality: 'friction', feel: 'minor conflict that nags at you', kind: 'subtle' },
  Semisextile: { quality: 'connection', feel: 'a quiet thread between two otherwise separate parts', kind: 'subtle' },
};

export const ASPECTS = [
  { name: 'Conjunction',    angle: 0,     orb: 8, color: 'hsl(45 30% 70%)', symbol: '☌', major: true },
  { name: 'Opposition',     angle: 180,   orb: 8, color: 'hsl(8 50% 55%)',  symbol: '☍', major: true },
  { name: 'Trine',          angle: 120,   orb: 7, color: 'hsl(210 40% 60%)', symbol: '△', major: true },
  { name: 'Square',         angle: 90,    orb: 7, color: 'hsl(15 55% 55%)', symbol: '□', major: true },
  { name: 'Sextile',        angle: 60,    orb: 5, color: 'hsl(135 30% 55%)', symbol: '⚹', major: true },
  { name: 'Quincunx',       angle: 150,   orb: 3, color: 'hsl(35 30% 60%)', symbol: '⚻', major: false },
  { name: 'Sesquiquadrate', angle: 135,   orb: 2, color: 'hsl(20 40% 50%)', symbol: '⚼', major: false },
  { name: 'Semisquare',     angle: 45,    orb: 2, color: 'hsl(20 40% 50%)', symbol: '∠', major: false },
  { name: 'Semisextile',    angle: 30,    orb: 2, color: 'hsl(135 30% 55%)', symbol: '⚺', major: false },
] as const;

export const GLOSSARY: Record<string, string> = {
  'Sun sign': 'The zodiac sign the Sun was in when you were born. Your "core self".',
  'Moon sign': 'The zodiac sign the Moon was in when you were born. Your emotional nature.',
  'Rising sign': 'Also called the Ascendant. How you come across to others — your "social skin."',
  'Ascendant': 'Same as "Rising sign." How you appear to others.',
  'Midheaven': 'The highest point of the sky at your birth. Career and reputation.',
  'House': 'One of 12 slices of the sky representing different areas of life.',
  'Aspect': 'A geometric angle between two planets describing how they interact.',
  'Orb': 'How close an aspect is to being exact. Smaller orb = stronger aspect.',
  'Retrograde': "When a planet appears to move backward from Earth's perspective.",
  'Transit': 'The current position of a planet and how it interacts with your natal chart.',
  'Synastry': "The comparison of two people's charts to understand compatibility.",
  'Natal chart': 'The map of the sky at the exact moment and place of your birth.',
};